<template>
    <div :id="id" class="s-header-product-2025" :class="className">
        <s-sticky-menu
            :product-data="productData"
            :navigation-title="productName"
            :navigation-subtitle="navigationSubtitle"
            :is-subpage="isSubpage"
            :event="event"
            :theme="theme"
            :buttons="buttons.slice(-2)"
            :settings="settings"
        />

        <div class="background a-container" :class="[mobileBackgroundStyle, centerBackgroundOnDesktop ? 'centered' : '']">
            <a-picture
                :class="{ 'subpage': isSubpage }"
                :background="background"
                is-background
                :fit="backgroundFit"
            />
            <div class="text-gradient" />
        </div>

        <div class="main-content a-container">
            <div class="main-content-info">
                <s-main-content
                    :action-links="actionLinks"
                    :product-data="productData"
                    :title="contentTitle"
                    :subtitle="contentSubtitle"
                    :description="l10n.description"
                    :label="l10n.label"
                    :additional-label="l10n.additionalLabel"
                    :cta-settings="ctaSettings"
                    :center-align="centerAlign"
                    :theme="theme"
                    :event="event"
                    v-bind="extraContent"
                />
                <div v-if="awardItems.length" class="awards awards-desktop">
                    <div
                        v-for="(award, i) in awardItems"
                        :key="i"
                        class="award"
                        :class="{ featured: award.featured }"
                    >
                        <p>
                            <a-picture v-if="award.image" :link="award.image" :alt="award.title || ''" />
                        </p>
                        <p v-if="award.description && award.featured" v-html="award.description" />
                    </div>
                </div>
            </div>

            <div
                v-if="l10n.form || l10n.media || l10n.mediaGallery.length"
                class="side-content"
                :class="{ 'with-background': mobileBackgroundStyle === 'side-content' }"
                :style="sideContentStyle"
            >
                <div v-if="mobileBackgroundStyle === 'side-content'" class="background">
                    <a-picture :background="background" is-background />
                </div>

                <s-trial-form v-if="l10n.form" :form="l10n.form" />
                <s-media v-else-if="l10n.media" v-bind="l10n.media" />
                <s-media-gallery
                    v-else-if="l10n.mediaGallery.length"
                    :gallery="l10n.mediaGallery"
                    :theme="theme"
                />
            </div>

            <div v-if="awardItems.length" class="awards awards-mobile">
                <div
                    v-for="(award, i) in awardItems"
                    :key="i"
                    class="award"
                    :class="{ featured: award.featured }"
                >
                    <p>
                        <a-picture v-if="award.image" :link="award.image" :alt="award.title || ''" />
                    </p>
                    <p v-if="award.description && award.featured" v-html="award.description" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Loading from '@uikit/ui-kit/packages/loading/src/directive.js';
import SMedia from '@core/components/media/media.vue';
import APicture from '@core/components/picture/picture.vue';
import analytics from '@core/mixins/analytics.js';
import breakpoint from '@core/mixins/breakpoint.js';
import cleverbridge from '@core/mixins/cleverbridge.js';
import contentMixin from '@core/mixins/content.js';
import l10n from '@core/mixins/l10n.js';
import purchasing from '@core/mixins/purchasing.js';
import STrialForm from '@core/slices/pages/trial-form/trial-form.vue';
import SMainContent from './components/main-content.vue';
import SMediaGallery from './components/media-gallery.vue';
import SStickyMenu from './components/sticky-menu.vue';
import mixin from './mixin.js';

export default {
    name: 'SHeaderProduct2025',

    components: {
        SStickyMenu,
        SMainContent,
        STrialForm,
        APicture,
        SMediaGallery,
        SMedia,
    },

    directives: { Loading },

    mixins: [l10n, contentMixin, mixin, purchasing, analytics, cleverbridge, breakpoint],

    props: {
        background: {
            type: Object,
            required: true,
        },

        /**
         * Background style on mobile:
         * default - image at the very bottom of the header;
         * side-content - image is under the side content (media, gallery, form);
         * fullsize - image takes the whole header.
         */
        mobileBackgroundStyle: {
            type: String,
            validator: (prop) => ['default', 'side-content', 'fullsize'].includes(prop),
            default: 'default',
        },

        /**
         * Optional centering of the background on desktop (needed on some old pages)
         */
        centerBackgroundOnDesktop: {
            type: Boolean,
            default: false,
        },

        /**
         * Optional gradient on top of the image
         */
        gradientOnTop: {
            type: Boolean,
            default: true,
        },

        /**
         * Optional gradient under the text part of header
         */
        gradientUnderText: {
            type: Boolean,
            default: true,
        },

        /**
         * Optional key visuals. 'centerBackgroundOnDesktop' is ignored if true
         */
        hasKeyVisuals: {
            type: Boolean,
            default: false,
        },

        /**
         * Optional margin for the side content (media, gallery, form) when key visuals are enabled
         */
        keyVisualsMargin: {
            type: Number,
            default: 392,
        },

        centerAlign: {
            type: Boolean,
            default: false,
        },

        productId: {
            type: Number,
            default: null,
        },

        event: {
            type: Object,
            default: () => ({
                category: 'Conversions',
                location: 'Top CTA',
            }),
        },

        title: {
            type: String,
            default: '',
        },

        subtitle: {
            type: String,
            default: null,
        },

        isSubpage: {
            type: Boolean,
            default: false,
        },

        isShort: {
            type: Boolean,
            default: false,
        },

        settings: {
            type: Object,
            default: undefined,
        },

        productPageCTA: {
            type: Object,
            default: undefined,
        },

        additionalLabel: {
            type: String,
            default: null,
        },

        groupId: {
            type: String,
            default: '',
        },

        label: {
            type: Object,
            default: null,
        },

        description: {
            type: String,
            default: '',
        },

        awards: {
            type: Array,
            default: () => [],
        },

        featuredAward: {
            type: Number,
            default: null,
        },

        productCaption: {
            type: String,
            default: undefined,
        },

        form: {
            type: Object,
            default: null,
        },

        media: {
            type: Object,
            default: null,
        },

        mediaGallery: {
            type: Array,
            default: () => [],
        },

        companyNameFallback: {
            type: String,
            default: 'Service Providers',
        },

        theme: {
            type: String,
            validator: (prop) => ['light', 'dark'].includes(prop),
            default: 'dark',
        },
    },

    data: () => ({
        isMounted: false,
    }),

    computed: {
        className() {
            return {
                'is-subpage': this.isSubpage,
                'mobile-bottom-padding': this.mobileBackgroundStyle !== 'default',
                'center-align': this.centerAlign,
                'with-top-gradient': this.gradientOnTop,
                'with-main-gradient': this.gradientUnderText,
                'with-key-visuals': this.hasKeyVisuals,
                'light': this.theme === 'light',
            };
        },

        sideContentStyle() {
            const top = this.hasKeyVisuals && this.isDesktop && !this.centerAlign
                ? `${this.keyVisualsMargin}px` : 0;
            return { 'margin-top': top };
        },

        backgroundFit() {
            if (this.centerBackgroundOnDesktop && this.isDesktop) return 'contain';

            return 'cover';
        },

        ctaSettings() {
            if (this.isSubpage && !this.isShort) return null;

            return {
                ...this.l10n.productPageCTA,
                labelTheme: this.l10n.productPageCTA?.labelTheme || 'dark',
                hasDescription: true,
                hasLabel: true,
            };
        },

        awardItems() {
            return [
                ...this.l10n.awards.map((item) => ({ ...this.$store.state.awards.items[item] })),
                this.l10n.featuredAward && { ...this.$store.state.awards.items[this.featuredAward], featured: true },
            ].filter(Boolean);
        },

        actionLinks() {
            return this.l10n.settings?.actionLinks || this.product?.actionLinks || [];
        },

        buttons() {
            return this.l10n.settings?.buttons || this.product?.buttons || [];
        },

        extraContent() {
            if (this.isSubpage && !this.isShort && !this.l10n.form) return {};

            const buttons = (this.l10n.form ? this.buttons?.slice(-1) : this.buttons) || [];
            const actionLinks = this.isShort ? [] : this.l10n.actionLinks;

            return {
                actionLinks,
                buttons,
            };
        },

        productData() {
            const productId = this.l10n.productId || this.internalProductId;
            return this.productItems?.[productId] || {};
        },

        product() {
            return this.productData?.parameters?.common || {};
        },

        navigationSubtitle() {
            return this.l10n.productCaption || this.product.productCaption;
        },

        contentTitle() {
            const titleSource = this.l10n.title || this.product.title || '';
            let path = this.$route.path;

            if (!path.includes('products/cloud/cyber-protect')) {
                return titleSource;
            }

            if (!this.isMounted) {
                return titleSource.includes('%COMPANY_NAME%') ? '' : titleSource;
            }

            if (path.includes('products/cloud/cyber-protect/pricing/hello') && titleSource.includes('%COMPANY_NAME%')) {
                const companyName = this.$store.state.trial?.queryStringData?.company || this.companyNameFallback;
                path = path.replace('hello/', '');
                this.$router.replace({ path });
                return titleSource.replace('%COMPANY_NAME%', companyName);
            }

            return titleSource;
        },

        contentSubtitle() {
            return this.l10n.subtitle || this.product.subtitle;
        },
    },

    async mounted() {
        this.isMounted = true;
        await this.$nextTick();
        this.handleResize();
    },

    async serverPrefetch() {
        const awards = [...this.l10n.awards, this.l10n.featuredAward].filter(Boolean);
        if (!awards) return;
        const promises = awards.map((id) => this.$store.dispatch('awards/getAward', id));
        await Promise.all(promises);
    },
};
</script>
<style lang="postcss" scoped>
.s-header-product-2025 {
    position: relative;
    display: flex;
    flex-direction: column;
    padding-top: 88px;
    background: var(--av-nav-primary);
    color: var(--av-inversed-primary);

    @media (--viewport-mobile-wide) {
        min-height: 500px;
    }

    @media (--viewport-desktop) {
        padding-top: 85px;
        padding-bottom: 48px;
    }

    @media (--viewport-desktop-wide) {
        padding-top: 96px;
    }

    @media (--viewport-desktop-large) {
        padding-top: 88px;
    }

    &.is-subpage {
        @media (--viewport-mobile-wide) {
            min-height: 357px;
        }
    }

    &.mobile-bottom-padding {
        @media (max-width: 1023px) {
            padding-bottom: 48px;
        }
    }

    &.center-align {
        .main-content {
            @media (--viewport-desktop) {
                flex-direction: column;
            }
        }

        .main-content-info {
            width: 100%;
            margin-inline: auto;

            @media (--viewport-mobile-wide) {
                width: 83%;
            }
            @media (--viewport-mobile-large) {
                width: 67%;
            }
        }

        .media-wrapper,
        .s-media-gallery {
            @media (--viewport-desktop) {
                max-width: 808px;
            }
            @media (--viewport-desktop-wide) {
                max-width: 928px;
            }
        }

        .awards {
            justify-content: center;
            .award {
                flex-direction: column;
                gap: 16px;
            }
        }
    }

    &.with-top-gradient {
        .background.fullsize.a-container {
            &:after {
                content: '';
                position: absolute;
                display: block;
                height: 134px;
                inset-inline: 0;
                top: 0;
                background: linear-gradient(180deg, rgba(0, 32, 77, 1) 0%, rgba(0, 32, 77, 0) 100%);
            }
        }

        @media (--viewport-desktop) {
            .background.a-container {
                &:after {
                    content: '';
                    position: absolute;
                    display: block;
                    height: 134px;
                    inset-inline: 0;
                    top: 0;
                    background: linear-gradient(180deg, rgba(0, 32, 77, 1) 0%, rgba(0, 32, 77, 0) 100%);
                }
            }
        }
    }

    &.with-main-gradient:not(.light) {
        @media (--viewport-desktop) {
            .background.a-container {
                .text-gradient{
                    position: absolute;
                    display: block;
                    height: 100%;
                    inset-inline: 0 400px;
                    top: 0;
                    background: linear-gradient(90deg, #00204d 0%, rgba(0, 32, 77, 0.68) 48.5%, rgba(0, 32, 77, 0.00) 100%);
                }
            }
        }
    }

    &.with-key-visuals {
        @media (--viewport-desktop) {
            .background.a-container {
                padding: 0;
            }

            .background .a-picture {
                overflow: hidden;
            }

            .background .a-picture:deep(.a-picture__img) {
                object-fit: cover;
                max-width: none;
                width: auto;
                top: 0;
                left: 50%;
                right: 0;
                height: fit-content;
                transform: translateX(-50%);
            }
        }
    }

    &.light {
        background: var(--av-brand-lightest);
        color: var(--av-nav-primary);
    }

    &.light.with-top-gradient {
        .background.a-container {
            &:after {
                display: none;
            }
        }
    }
}

.loading {
    height: 100vh;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.main-content {
    display: flex;
    column-gap: 32px;
    row-gap: 40px;
    margin-top: auto;
    flex-direction: column;

    @media (--viewport-desktop) {
        flex-direction: row;
        align-items: center;
    }
}

.main-content-info {
    @media (--viewport-desktop) {
        width: 50%;
    }
}

.side-content {
    @media (max-width: 1023px) {
        margin-top: 0 !important;
    }

    &:deep(.a-container) {
        padding-block: 0;

        @media (--viewport-mobile-wide) {
            padding-inline: 0;
        }
    }

    &:deep(.s-form) {
        padding: 0;
    }

    &:deep(.s-form__wrapper) {
        margin-inline: auto;
    }

    &:deep(.s-form-container) {
        margin-top: 0;
    }

    &.with-background {
        position: relative;
        padding-block: 84px;
        margin-bottom: -48px;
        margin-inline: -16px;

        @media (--viewport-mobile-wide) {
            margin-inline: -32px;
        }

        @media (--viewport-desktop) {
            margin-bottom: 0;
            margin-inline: 0;
            padding-block: 0;
        }

        + .awards {
            margin-top: 40px;
        }
    }

    .background {
        @media (--viewport-desktop) {
            display: none;
        }
    }
}

.media-wrapper {
    width: 100%;
}

.side-content:deep(.media-wrapper .media .screenshot-image) {
    opacity: 1;
}

.side-content:deep(.s-form-container),
.media-wrapper,
.s-media-gallery {
    margin: 0 auto;
    max-width: 472px;

    @media (--viewport-tablet) {
        max-width: 648px;
    }

    @media (--viewport-desktop) {
        max-width: 472px;
    }

    @media (--viewport-desktop-wide) {
        max-width: 568px;
    }

    @media (--viewport-desktop-large) {
        max-width: 648px;
    }
}

.background.a-container {
    @media (max-width: 1023px) {
        &.default {
            position: relative;
            width: 100%;
            height: 240px;
            margin-top: 40px;
            padding: 0;
            order: 1;
        }

        &.fullsize {
            order: 0;
            top: 85px;
            position: absolute;
            inset-inline-start: 0;
            inset-inline-end: 0;
            width: 100%;
            margin: auto;
            height: calc(100% - 85px);
            padding: 0;
            max-width: none;

            .a-picture {
                object-position: center bottom !important;
            }
        }

        &.side-content {
            display: none;
        }
    }

    @media (--viewport-desktop) {
        order: 0;
        top: 85px;
        position: absolute;
        inset-inline-start: 0;
        inset-inline-end: 0;
        width: 100%;
        margin: auto;
        height: calc(100% - 85px);

        &:not(.centered) {
            padding: 0;
            max-width: none;
        }
    }

    @media (--viewport-desktop-wide) {
        top: 96px;
        height: calc(100% - 96px);
    }

    @media (--viewport-desktop-large) {
        top: 88px;
        height: calc(100% - 88px);
    }

    .a-picture {
        position: relative;
        width: 100%;
        height: 100%;

        @media (--viewport-desktop) {
            &:deep(.a-picture__img) {
                object-position: center bottom !important;
            }
        }
    }
}

.awards {
    @mixin paragraph;
    position: relative;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    row-gap: 16px;
    column-gap: 40px;
    justify-content: center;

    @media (--viewport-desktop) {
        margin-top: 40px;
        justify-content: flex-start;
    }

    &.awards-desktop {
        display: none;
        @media (--viewport-desktop) {
            display: flex;
        }
    }
    &.awards-mobile {
        @media (--viewport-desktop) {
            display: none;
        }
    }

    .award {
        gap: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;

        &.featured {
            margin-top: 24px;
            width: 100%;
        }

        @media (--viewport-desktop) {
            gap: 32px;
            flex-direction: row;
            text-align: start;
        }
    }

    &:deep(.a-picture) {
        height: 48px;
        max-width: 126px;
        display: flex;
        align-items: center;

        .a-picture__img {
            margin: 0;
        }
    }
}
</style>
